import React, { Suspense } from "react";
import { createPortal } from "react-dom";

const EmojiPanel = React.lazy(() => import("../media/EmojiPanel"));

import type { PendingUpload } from "../../features/chat/composer/useUploads";
type ReplyInfo = { authorName?: string; authorColor?: string | null; content?: string } | null;
type MentionMember = { id: string; name?: string; username?: string; avatarUrl?: string | null };
type MentionOption = MentionMember & { token: string; isAll?: boolean };

type Props = {
  pendingUploads: PendingUpload[];
  onRemoveUpload: (idx: number) => void;
  uploading?: boolean;
  uploadError?: string | null;
  addOpen: boolean;
  onToggleAdd: () => void;
  onTouchToggleAdd: (e: React.TouchEvent<HTMLButtonElement>) => void;
  addBtnRef: React.RefObject<HTMLButtonElement | null>;
  addMenuRef: React.RefObject<HTMLDivElement | null>;
  spoiler: boolean;
  onToggleSpoiler: () => void;
  onOpenGif: () => void;
  onFilesSelected: (files: FileList | null) => void;
  replyTo: ReplyInfo;
  onCancelReply: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  composerPlaceholder: string;
  text: string;
  onInputChange: (val: string) => void;
  mentionMembers?: MentionMember[];
  onComposerPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  composerPickerOpen: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  addRecentEmoji: (emoji: string) => void;
  insertEmojiAtCaret: (emoji: string) => void;
  quickEmojis?: string[];
  keyboardInset: number;
  onHeightChange?: (height: number) => void;
};

export default function Composer({
  pendingUploads,
  onRemoveUpload,
  addOpen,
  onToggleAdd,
  onTouchToggleAdd,
  addBtnRef,
  addMenuRef,
  uploading = false,
  uploadError = null,
  spoiler,
  onToggleSpoiler,
  onOpenGif,
  onFilesSelected,
  replyTo,
  onCancelReply,
  inputRef,
  composerPlaceholder,
  text,
  onInputChange,
  mentionMembers = [],
  onComposerPaste,
  onSend,
  composerPickerOpen,
  onOpenPicker,
  onClosePicker,
  addRecentEmoji,
  insertEmojiAtCaret,
  quickEmojis,
  keyboardInset,
  onHeightChange,
}: Props) {
  const [mobileMenuStyle, setMobileMenuStyle] = React.useState<React.CSSProperties | null>(null);
  const isCoarseInput = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const nav: any = typeof navigator !== "undefined" ? navigator : {};
    const hasCoarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer:coarse)").matches;
    const hasTouch = "ontouchstart" in window || Number(nav.maxTouchPoints || 0) > 0;
    return hasCoarse || hasTouch;
  }, []);
  const lastSendRef = React.useRef<number>(0);
  const [mentionTrigger, setMentionTrigger] = React.useState<{ start: number; end: number; query: string } | null>(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = React.useState(0);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const cameraInputRef = React.useRef<HTMLInputElement | null>(null);
  const openFilePicker = () => { fileInputRef.current?.click(); };
  const openCameraPicker = () => { cameraInputRef.current?.click(); };
  const handleFileTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    openFilePicker();
  };
  const handleCameraTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    openCameraPicker();
  };

  React.useLayoutEffect(() => {
    if (!addOpen) {
      setMobileMenuStyle(null);
      return;
    }
    const positionMenu = () => {
      if (typeof window === "undefined" || window.innerWidth >= 768) {
        setMobileMenuStyle(null);
        return;
      }
      const btn = addBtnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setMobileMenuStyle({
        position: "fixed",
        left: Math.max(8, rect.left),
        bottom: Math.max(8, window.innerHeight - rect.top + 8),
        zIndex: 160,
      });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.visualViewport?.addEventListener("resize", positionMenu);
    window.visualViewport?.addEventListener("scroll", positionMenu);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.visualViewport?.removeEventListener("resize", positionMenu);
      window.visualViewport?.removeEventListener("scroll", positionMenu);
    };
  }, [addBtnRef, addOpen]);

  React.useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => {
      try {
        const styles = window.getComputedStyle(el);
        const padBottom = parseFloat(styles.paddingBottom || "0") || 0;
        const padTop = parseFloat(styles.paddingTop || "0") || 0;
        const height = Math.max(0, (el.offsetHeight || 0) - padBottom - padTop);
        onHeightChange?.(height);
      } catch {
        /* ignore measure errors */
      }
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    }
    const id = window.setInterval(measure, 500);
    return () => window.clearInterval(id);
  }, [onHeightChange, keyboardInset]);

  const assumedSafeAreaPx = 24; // approximate nav/safe area; subtract from keyboard inset so nav bar doesn't lift composer
  const keyboardOverflowPx = Math.max(0, keyboardInset - assumedSafeAreaPx);
  const shouldApplyKeyboardInset = React.useMemo(() => {
    if (typeof window === "undefined") return true;
    const vv = window.visualViewport;
    if (!vv) return true;
    const delta = Math.max(0, window.innerHeight - vv.height);
    // If the viewport already shrank for the keyboard, don't add extra padding.
    return delta === 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyboardInset is a deliberate trigger: the memo re-reads window.visualViewport when it changes
  }, [keyboardInset]);
  const keyboardPadPx = shouldApplyKeyboardInset ? keyboardOverflowPx : 0;
  const mentionOptions = React.useMemo<MentionOption[]>(() => {
    if (!mentionTrigger) return [];
    const query = mentionTrigger.query.toLowerCase();
    const allOption: MentionOption = { id: "__all__", name: "Everyone", username: "all", token: "all", isAll: true };
    const userOptions = mentionMembers
      .map((member) => ({
        ...member,
        token: String(member.username || member.name || "").trim(),
      }))
      .filter((member) => member.token)
      .filter((member) => {
        if (!query) return true;
        return member.token.toLowerCase().includes(query) || String(member.name || "").toLowerCase().includes(query);
      });
    const options = (!query || "all".includes(query)) ? [allOption, ...userOptions] : userOptions;
    return options.slice(0, 8);
  }, [mentionMembers, mentionTrigger]);
  const showMentions = !!mentionTrigger && mentionOptions.length > 0;

  React.useEffect(() => {
    if (!text) setMentionTrigger(null);
  }, [text]);

  const updateMentionTrigger = (value: string, caret: number) => {
    const beforeCaret = value.slice(0, caret);
    const match = beforeCaret.match(/(?:^|\s)@([^\s@]*)$/);
    if (!match) {
      setMentionTrigger(null);
      return;
    }
    const query = match[1] || "";
    setMentionTrigger({ start: caret - query.length - 1, end: caret, query });
    setSelectedMentionIndex(0);
  };

  const selectMention = (option: MentionOption) => {
    if (!mentionTrigger) return;
    const insertion = `@${option.token} `;
    const next = text.slice(0, mentionTrigger.start) + insertion + text.slice(mentionTrigger.end);
    const caret = mentionTrigger.start + insertion.length;
    onInputChange(next);
    setMentionTrigger(null);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(caret, caret);
    });
  };

  const addMenu = addOpen ? (
    <div
      ref={addMenuRef}
      className={`${mobileMenuStyle ? "" : "absolute bottom-full left-0 z-40 mb-2"} flex gap-1 rounded-xl border border-white/10 bg-neutral-900/95 p-2 shadow-2xl`}
      style={mobileMenuStyle || undefined}
    >
      <button
        className={`rounded-lg px-2 py-1 text-sm ${spoiler ? "bg-amber-800/40 text-amber-100" : "text-fg hover:bg-white/10"}`}
        onClick={onToggleSpoiler}
        title="Toggle spoiler"
        aria-pressed={spoiler}
        aria-label="Toggle spoiler"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      </button>
      <button className="rounded-lg px-2 py-1 text-sm text-fg hover:bg-white/10" onClick={onOpenGif} title="GIF">
        GIF
      </button>
      <button
        className="rounded-lg px-2 py-1 text-sm text-fg hover:bg-white/10"
        title="File"
        onClick={openFilePicker}
        onTouchStart={handleFileTouchStart}
      >
        File
      </button>
      <button
        className="rounded-lg px-2 py-1 text-sm text-fg hover:bg-white/10"
        title="Camera"
        onClick={openCameraPicker}
        onTouchStart={handleCameraTouchStart}
      >
        Camera
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ position: "fixed", opacity: 0, width: 1, height: 1, left: -9999, top: -9999 }}
        onChange={(e) => onFilesSelected(e.target.files)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ position: "fixed", opacity: 0, width: 1, height: 1, left: -9999, top: -9999 }}
        onChange={(e) => onFilesSelected(e.target.files)}
      />
    </div>
  ) : null;

  return (
    <div
      ref={wrapperRef}
      className="fixed bottom-0 left-0 right-0 z-40 px-3 sm:px-4 pb-4 pt-4 bg-transparent border-none md:relative md:bottom-auto md:left-auto md:right-auto md:z-auto md:bg-transparent md:border-transparent md:pb-0"
      style={{
        paddingBottom: `calc(env(safe-area-inset-bottom) + ${keyboardPadPx}px + 0.5rem)`,
        boxSizing: 'border-box',
        overscrollBehavior: 'contain',
        touchAction: 'none',
      }}
    >
      {pendingUploads.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {pendingUploads.map((a, i) => {
            const isImg = (() => {
              if (a.contentType && a.contentType.startsWith("image/")) return true;
              const u = (a.url || a.localPreviewUrl || "").toLowerCase();
              const n = (a.name || "").toLowerCase();
              return /(\.png|\.jpe?g|\.gif|\.webp|\.svg)(\?.*)?$/.test(u) || /(\.png|\.jpe?g|\.gif|\.webp|\.svg)(\?.*)?$/.test(n);
            })();
            const previewSrc = a.localPreviewUrl || a.url;
            return (
              <div key={i} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-neutral-950/70 px-3 py-1.5">
                {isImg ? (
                  <div className="relative h-10 w-10 shrink-0 rounded overflow-hidden">
                    <img src={previewSrc} alt={a.name || ""} className="h-full w-full object-cover" />
                    {a.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <svg className="h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-fg-2">
                    <path d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a2 2 0 1 1-2.83-2.83l8.49-8.49" />
                  </svg>
                )}
                {!isImg && <span className="text-sm text-fg">{a.name}</span>}
                <button
                  aria-label="Remove attachment"
                  className="ml-1 rounded-full border border-neutral-700 px-1.5 py-0.5 text-fg-2 hover:border-red-500 hover:text-red-200"
                  onClick={() => onRemoveUpload(i)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
      {uploading && (
        <div className="mb-2 text-xs text-amber-200">Uploading…</div>
      )}
      {uploadError && (
        <div className="mb-2 text-xs text-red-300">Upload failed: {uploadError}</div>
      )}
      <div className="relative flex w-full max-w-full items-center gap-1 md:gap-2">
        <div
          className="composer-field flex w-full items-center gap-1 p-1"
        >
          <button
            ref={addBtnRef}
            type="button"
            className="composer-icon-btn"
            title="Add"
            aria-label="Add"
            onClick={onToggleAdd}
            onTouchStart={onTouchToggleAdd}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          {addMenu && mobileMenuStyle && typeof document !== "undefined" ? createPortal(addMenu, document.body) : addMenu}
          {replyTo && (
            <div className="absolute -top-10 left-0 right-0 mb-1 flex items-center justify-between rounded-2xl border border-white/10 bg-neutral-900/80 px-3 py-1 text-xs text-fg">
              <div className="truncate">
                Replying to <span style={{ color: (replyTo as any)?.authorColor || "#34d399" }}>{replyTo.authorName || "User"}</span>:{" "}
                <span className="text-fg-2">{(replyTo.content || "").slice(0, 80)}</span>
              </div>
              <button className="ml-2 text-fg-2 hover:text-fg" onClick={onCancelReply} aria-label="Cancel reply" title="Cancel reply">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
          {showMentions && (
            <div className="absolute bottom-full left-12 right-14 z-50 mb-2 overflow-hidden rounded-xl border border-emerald-400/15 bg-neutral-950/95 p-1 backdrop-blur">
              <div className="px-2 py-1 text-meta text-emerald-300/70">Mention</div>
              {mentionOptions.map((option, index) => (
                <button
                  type="button"
                  key={option.id}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition ${
                    index === selectedMentionIndex ? "bg-emerald-400/10 text-emerald-100" : "text-fg hover:bg-white/5"
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectMention(option);
                  }}
                >
                  {option.isAll ? (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-semibold text-emerald-200">@</div>
                  ) : option.avatarUrl ? (
                    <img src={option.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-xs text-fg">
                      {(option.name || option.token).slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{option.isAll ? "@all" : (option.name || option.token)}</span>
                    <span className="block truncate text-xs text-fg-3">
                      {option.isAll ? "Notify everyone in this channel" : `@${option.token}`}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          <textarea
            ref={inputRef}
            rows={1}
            className="composer-input min-w-0 flex-1 resize-none border-0 bg-transparent px-1 py-1 text-base text-fg outline-none placeholder:text-fg-3 text-left leading-7"
            placeholder={composerPlaceholder}
            aria-label={composerPlaceholder}
            value={text}
            onChange={(e) => {
              onInputChange(e.target.value);
              updateMentionTrigger(e.target.value, e.target.selectionStart ?? e.target.value.length);
            }}
            onPaste={onComposerPaste}
            onFocus={() => {
              if (isCoarseInput) return;
              requestAnimationFrame(() => {
                inputRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
              });
            }}
            onKeyDown={(e) => {
              if (showMentions) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setSelectedMentionIndex((current) => (current + 1) % mentionOptions.length);
                  return;
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setSelectedMentionIndex((current) => (current - 1 + mentionOptions.length) % mentionOptions.length);
                  return;
                }
                if (e.key === "Tab" || e.key === "Enter") {
                  e.preventDefault();
                  selectMention(mentionOptions[selectedMentionIndex] || mentionOptions[0]);
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setMentionTrigger(null);
                  return;
                }
              }
              if (e.key === "Enter" && !e.shiftKey && !uploading) {
                if (isCoarseInput) {
                  // On touch/mobile, Enter should insert a newline instead of sending
                  return;
                }
                e.preventDefault();
                onSend();
              }
            }}
            autoComplete="off"
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck={true}
            name="chat-message"
            data-lpignore="true"
            data-1p-ignore
            data-bw-ignore
            data-form-type="other"
          />
          <button
            type="button"
            className="composer-icon-btn"
            title="Emoji"
            aria-label="Emoji"
            onClick={onOpenPicker}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <circle cx="12" cy="12" r="9" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <path d="M9 9h.01M15 9h.01" />
            </svg>
          </button>
          <button
            type="button"
            className="composer-send"
            data-ready={text.trim().length > 0 || pendingUploads.length > 0 ? "true" : "false"}
            title="Send"
            aria-label="Send"
            onClick={(e) => {
              const now = Date.now();
              if (now - lastSendRef.current < 250) return;
              lastSendRef.current = now;
              e.preventDefault();
              if (!uploading) onSend();
            }}
            onPointerUp={(e) => {
              const now = Date.now();
              if (now - lastSendRef.current < 250) return;
              lastSendRef.current = now;
              e.preventDefault();
              if (!uploading) onSend();
            }}
            disabled={uploading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      </div>
      {composerPickerOpen && (
        <Suspense>
          <EmojiPanel
            quickEmojis={quickEmojis}
            onClose={onClosePicker}
            onSelect={(native: string) => {
              addRecentEmoji(native);
              insertEmojiAtCaret(native);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
